import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';
import { SESSION_STATUSES, CHANGE_REASONS } from '../utils/constants';
import { formatDate, formatTime } from '../utils/timeFormat';
import './MentorDashboardPage.css';

function isPast(dateStr, timeStr) {
  if (!dateStr) return false;
  return new Date(`${dateStr}T${timeStr || '23:59'}`) < new Date();
}

function isHistorySession(req) {
  return (
    req.status === SESSION_STATUSES.COMPLETED ||
    req.status === SESSION_STATUSES.DECLINED ||
    req.status === SESSION_STATUSES.CANCELLED ||
    (req.status === SESSION_STATUSES.PAID && isPast(req.proposed_date, req.proposed_time))
  );
}

function getDisplayStatus(req) {
  if (req.status === SESSION_STATUSES.PAID && !isPast(req.proposed_date, req.proposed_time)) return 'scheduled';
  if (req.status === SESSION_STATUSES.PAID && isPast(req.proposed_date, req.proposed_time)) return 'completed';
  return req.status;
}

function StatusBadge({ status }) {
  const labels = {
    pending: 'Pending',
    accepted: 'Accepted',
    scheduled: 'Scheduled',
    completed: 'Completed',
    declined: 'Declined',
    cancelled: 'Cancelled',
    change_suggested: 'Change Suggested',
  };
  return <span className={`td-badge td-badge-${status}`}>{labels[status] || status}</span>;
}

function ChangeForm({ requestId, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({ newDate: '', newTime: '', newLocation: '', reason: '', note: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.reason) errs.reason = 'Please select a reason.';
    if (formData.note.length > 140) errs.note = 'Note must be 140 characters or fewer.';
    if (!formData.newDate && !formData.newTime && !formData.newLocation.trim()) {
      errs.newDate = 'Suggest at least one change (date, time, or location).';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    try { await onSubmit(requestId, formData); }
    finally { setSubmitting(false); }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <form className="td-change-form" onSubmit={handleSubmit} noValidate>
      <p className="td-change-form-hint">Suggest new details (leave blank to keep original):</p>
      <div className="td-change-form-row">
        <div className="td-form-group">
          <label className="td-form-label">New Date</label>
          <input name="newDate" type="date" min={today} className={`td-form-input${errors.newDate ? ' error' : ''}`} value={formData.newDate} onChange={handleChange} />
          {errors.newDate && <span className="td-form-error">{errors.newDate}</span>}
        </div>
        <div className="td-form-group">
          <label className="td-form-label">New Time</label>
          <input name="newTime" type="time" className="td-form-input" value={formData.newTime} onChange={handleChange} />
        </div>
      </div>
      <div className="td-form-group">
        <label className="td-form-label">New Location</label>
        <input name="newLocation" type="text" className="td-form-input" value={formData.newLocation} onChange={handleChange} placeholder="Leave blank to keep original" />
      </div>
      <div className="td-form-group">
        <label className="td-form-label">Reason *</label>
        <select name="reason" className={`td-form-select${errors.reason ? ' error' : ''}`} value={formData.reason} onChange={handleChange}>
          <option value="">Select a reason</option>
          {CHANGE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        {errors.reason && <span className="td-form-error">{errors.reason}</span>}
      </div>
      <div className="td-form-group">
        <label className="td-form-label">
          Note (Optional)
          <span className={`td-char-counter${formData.note.length > 140 ? ' over' : ''}`}>{formData.note.length}/140</span>
        </label>
        <textarea name="note" className={`td-form-textarea${errors.note ? ' error' : ''}`} value={formData.note} onChange={handleChange} placeholder="Any additional context…" rows={2} maxLength={150} />
        {errors.note && <span className="td-form-error">{errors.note}</span>}
      </div>
      <div className="td-change-form-actions">
        <button type="submit" className="td-btn-accept" disabled={submitting}>{submitting ? 'Sending…' : 'Send Change'}</button>
        <button type="button" className="td-btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function StatsBar({ requests, profile }) {
  const pendingCount = requests.filter(r => r.status === SESSION_STATUSES.PENDING).length;
  const upcoming = requests.filter(r => r.status === SESSION_STATUSES.PAID && !isPast(r.proposed_date, r.proposed_time)).length;
  const completed = requests.filter(r =>
    r.status === SESSION_STATUSES.COMPLETED ||
    (r.status === SESSION_STATUSES.PAID && isPast(r.proposed_date, r.proposed_time))
  ).length;
  const allRatings = requests.flatMap(r => r.ratings || []);
  const avgRating = allRatings.length > 0
    ? (allRatings.reduce((sum, r) => sum + r.rating, 0) / allRatings.length).toFixed(1)
    : null;

  return (
    <div className="td-stats">
      <div className="td-stat">
        <div className="td-stat-label">Requests</div>
        <div className={`td-stat-num${pendingCount > 0 ? ' td-stat-amber' : ''}`}>{pendingCount}</div>
      </div>
      <div className="td-stat">
        <div className="td-stat-label">Upcoming</div>
        <div className={`td-stat-num${upcoming > 0 ? ' td-stat-green' : ''}`}>{upcoming}</div>
      </div>
      <div className="td-stat">
        <div className="td-stat-label">Completed</div>
        <div className="td-stat-num">{completed}</div>
      </div>
      <div className="td-stat">
        <div className="td-stat-label">Avg Rating</div>
        <div className="td-stat-num td-stat-yellow">{avgRating ? `${avgRating} ★` : '—'}</div>
      </div>
    </div>
  );
}

function ConfirmForm({ message, placeholder, confirmLabel, confirmClass, onConfirm, onBack }) {
  const [reason, setReason] = React.useState('');
  return (
    <div className="td-confirm-form">
      <p className="td-confirm-form-msg">{message}</p>
      <textarea
        className="td-form-textarea"
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder={placeholder}
        rows={2}
        maxLength={200}
      />
      <div className="td-confirm-form-actions">
        <button className={`td-confirm-btn ${confirmClass}`} onClick={() => onConfirm(reason)}>{confirmLabel}</button>
        <button className="td-btn-ghost" onClick={onBack}>Go Back</button>
      </div>
    </div>
  );
}

function MentorSessionCard({ request, userId, profile, onAction }) {
  const { status } = request;
  const parent = request.parent;
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);

  if (request.mentor_id !== userId) return null;

  const displayStatus = getDisplayStatus(request);
  const ratings = request.ratings || [];
  const isScheduled = status === SESSION_STATUSES.PAID && !isPast(request.proposed_date, request.proposed_time);
  const isPastPaid = status === SESSION_STATUSES.PAID && isPast(request.proposed_date, request.proposed_time);
  const history = isHistorySession(request);

  const sessionTotal = profile?.hourly_rate && request.duration
    ? (Number(profile.hourly_rate) * Number(request.duration)).toFixed(0)
    : null;

  return (
    <div className={`td-card${history ? ' td-card-history' : ''}`}>
      <div className={`td-card-accent td-accent-${displayStatus}`} />
      <div className="td-card-body">

        <div className="td-card-head">
          <div className="td-parent-row">
            <div className="td-avatar-ph">{parent?.first_name?.[0]?.toUpperCase() || '?'}</div>
            <div>
              <span className="td-parent-name">{parent ? `${parent.first_name} ${parent.last_name}` : 'Parent'}</span>
              <StatusBadge status={displayStatus} />
            </div>
          </div>
          {ratings.length > 0 && history && (
            <div className="td-rating-display">
              <div className="td-stars">
                {[1, 2, 3, 4, 5].map(i => (
                  <span key={i} className={i <= ratings[0].rating ? 'td-star-filled' : 'td-star-empty'}>★</span>
                ))}
              </div>
              {ratings[0].comment && <div className="td-rating-comment">"{ratings[0].comment}"</div>}
            </div>
          )}
        </div>

        <div className="td-detail-grid">
          <div className="td-detail-cell"><div className="td-dk">Sport</div><div className="td-dv">{request.sport}</div></div>
          <div className="td-detail-cell"><div className="td-dk">Date</div><div className="td-dv">{formatDate(request.proposed_date)}</div></div>
          <div className="td-detail-cell"><div className="td-dk">Time</div><div className="td-dv">{formatTime(request.proposed_time)}</div></div>
          <div className="td-detail-cell"><div className="td-dk">Location</div><div className="td-dv">{request.proposed_location}</div></div>
          {request.duration && (
            <div className="td-detail-cell"><div className="td-dk">Duration</div><div className="td-dv">{request.duration} {Number(request.duration) === 1 ? 'hour' : 'hours'}</div></div>
          )}
          {request.child_age_range && (
            <div className="td-detail-cell"><div className="td-dk">Athlete Age</div><div className="td-dv">{request.child_age_range} yrs</div></div>
          )}
          {sessionTotal && (
            <div className="td-detail-cell"><div className="td-dk">You'd Earn</div><div className="td-dv">${sessionTotal}</div></div>
          )}
          {request.notes && (
            <div className="td-detail-cell td-detail-cell-full"><div className="td-dk">Notes</div><div className="td-dv">{request.notes}</div></div>
          )}
        </div>

        {showChangeForm && (
          <ChangeForm
            requestId={request.id}
            onSubmit={(id, data) => { onAction('suggest', id, data); setShowChangeForm(false); }}
            onCancel={() => setShowChangeForm(false)}
          />
        )}

        <div className="td-card-foot">
          <div className="td-foot-msg">
            {status === SESSION_STATUSES.PENDING && !showChangeForm && 'New session request'}
            {status === SESSION_STATUSES.PENDING && showChangeForm && 'Suggest new details below'}
            {status === SESSION_STATUSES.CHANGE_SUGGESTED && 'Waiting for parent to respond'}
            {status === SESSION_STATUSES.ACCEPTED && 'Awaiting payment from parent'}
            {isScheduled && (
              <span className="td-foot-msg-green">
                Collect {sessionTotal ? `$${sessionTotal}` : 'your rate'} from {parent?.first_name || 'parent'} in person
              </span>
            )}
            {(isPastPaid || status === SESSION_STATUSES.COMPLETED) && 'Session completed'}
            {status === SESSION_STATUSES.DECLINED && (
              <span>{request.decline_reason ? `Declined: "${request.decline_reason}"` : 'You declined this request'}</span>
            )}
            {status === SESSION_STATUSES.CANCELLED && (
              <span>{request.cancel_reason ? `Cancelled: "${request.cancel_reason}"` : 'Session cancelled'}</span>
            )}
          </div>

          <div className="td-actions">
            {status === SESSION_STATUSES.PENDING && !showChangeForm && (
              <>
                <button className="td-btn-ghost" onClick={() => setShowChangeForm(true)}>Suggest Change</button>
                <button className="td-btn-decline" onClick={() => setShowDeclineForm(true)}>Decline</button>
                <button className="td-btn-accept" onClick={() => onAction('accept', request.id)}>Accept</button>
              </>
            )}
            {isScheduled && !showCancelForm && (
              <button className="td-btn-ghost" onClick={() => setShowCancelForm(true)}>Cancel Session</button>
            )}
          </div>
        </div>
        {showDeclineForm && (
          <ConfirmForm
            message="Are you sure you want to decline this request?"
            placeholder="Reason for declining (optional — the parent will see this)"
            confirmLabel="Confirm Decline"
            confirmClass="td-btn-decline-confirm"
            onConfirm={(reason) => { onAction('decline', request.id, { reason }); setShowDeclineForm(false); }}
            onBack={() => setShowDeclineForm(false)}
          />
        )}
        {showCancelForm && (
          <ConfirmForm
            message="Are you sure you want to cancel this session?"
            placeholder="Reason for cancelling (optional — the parent will see this)"
            confirmLabel="Confirm Cancellation"
            confirmClass="td-btn-decline-confirm"
            onConfirm={(reason) => { onAction('cancel', request.id, { reason }); setShowCancelForm(false); }}
            onBack={() => setShowCancelForm(false)}
          />
        )}

      </div>
    </div>
  );
}

function MentorDashboardPage() {
  const { user, profile } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [activeTab, setActiveTab] = useState('active');

  const fetchRequests = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    setError('');
    if (process.env.NODE_ENV === 'development') {
      console.log('MentorDashboardPage: fetching for mentor_id:', user.id);
    }
    try {
      const queryPromise = supabase
        .from('session_requests')
        .select('*, parent:profiles!session_requests_parent_id_fkey(first_name, last_name), ratings(rating, comment)')
        .eq('mentor_id', user.id)
        .order('proposed_date', { ascending: true });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Please try again.')), 10000)
      );

      const result = await Promise.race([queryPromise, timeoutPromise]);
      const { data, error: err } = result;
      if (err) throw err;
      setRequests(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load sessions.');
      if (process.env.NODE_ENV === 'development') {
        console.error('MentorDashboard fetch error:', err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetchRequests();
  }, [fetchRequests, user]);

  const handleAction = async (action, requestId, changeData) => {
    setActionError('');
    const req = requests.find(r => r.id === requestId);
    if (!req || req.mentor_id !== user.id) {
      setActionError('Unauthorized action.');
      return;
    }
    try {
      if (action === 'accept') {
        const { error } = await supabase.from('session_requests').update({ status: SESSION_STATUSES.ACCEPTED }).eq('id', requestId).eq('mentor_id', user.id);
        if (error) throw error;
      } else if (action === 'decline') {
        const { error } = await supabase.from('session_requests').update({ status: SESSION_STATUSES.DECLINED, decline_reason: changeData?.reason || null }).eq('id', requestId).eq('mentor_id', user.id);
        if (error) throw error;
      } else if (action === 'cancel') {
        const { error } = await supabase.from('session_requests').update({ status: SESSION_STATUSES.CANCELLED, cancel_reason: changeData?.reason || null }).eq('id', requestId).eq('mentor_id', user.id);
        if (error) throw error;
      } else if (action === 'suggest') {
        const updates = {
          status: SESSION_STATUSES.CHANGE_SUGGESTED,
          change_reason: changeData.reason,
          change_note: changeData.note || null,
        };
        if (changeData.newDate) updates.suggested_date = changeData.newDate;
        if (changeData.newTime) updates.suggested_time = changeData.newTime;
        if (changeData.newLocation.trim()) updates.suggested_location = changeData.newLocation.trim();
        const { error } = await supabase.from('session_requests').update(updates).eq('id', requestId).eq('mentor_id', user.id);
        if (error) throw error;
      }
      await fetchRequests();
    } catch (err) {
      setActionError(err.message || 'Action failed. Please try again.');
      if (process.env.NODE_ENV === 'development') {
        console.error('MentorDashboard action error:', err.message);
      }
    }
  };

  const activeRequests = requests.filter(r => !isHistorySession(r));
  const historyRequests = requests.filter(r => isHistorySession(r));

  return (
    <div className="td-wrapper">
      <div className="page-container td-page">
        <div className="td-header">
          <div>
            <h1 className="td-title">My Sessions</h1>
            {profile?.sport && <p className="td-subtitle">{profile.sport} Trainer</p>}
          </div>
          <Link to={`/mentors/${user?.id}`} className="td-btn-outline">View My Profile</Link>
        </div>

        {actionError && <div className="alert alert-error">{actionError}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <div className="app-loading">
            <div className="app-loading-spinner" />
            <p style={{ marginTop: '0.75rem', color: '#8aaec8', fontFamily: 'Open Sans, sans-serif' }}>Loading your sessions…</p>
          </div>
        ) : (
          <>
            <StatsBar requests={requests} profile={profile} />

            <div className="td-tabs">
              <button className={`td-tab${activeTab === 'active' ? ' td-tab-active' : ''}`} onClick={() => setActiveTab('active')}>
                Active <span className="td-tab-badge">{activeRequests.length}</span>
              </button>
              <button className={`td-tab${activeTab === 'history' ? ' td-tab-active' : ''}`} onClick={() => setActiveTab('history')}>
                History <span className="td-tab-badge">{historyRequests.length}</span>
              </button>
            </div>

            {activeTab === 'active' && (
              activeRequests.length === 0 ? (
                <div className="td-empty">
                  <p>No active sessions yet.</p>
                  <Link to="/profile" className="btn btn-primary" style={{ marginTop: '1rem' }}>Complete Your Profile</Link>
                </div>
              ) : (
                activeRequests.map(req => (
                  <MentorSessionCard key={req.id} request={req} userId={user.id} profile={profile} onAction={handleAction} />
                ))
              )
            )}

            {activeTab === 'history' && (
              historyRequests.length === 0 ? (
                <div className="td-empty"><p>No past sessions yet.</p></div>
              ) : (
                historyRequests.map(req => (
                  <MentorSessionCard key={req.id} request={req} userId={user.id} profile={profile} onAction={handleAction} />
                ))
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default MentorDashboardPage;
